// ── Stage Save Bar ────────────────────────────────────────────────────────────
//
// Every configurable stage (IDV, KYC, KYB, AML, Fraud, TM, Workflow Builder)
// renders one of these so the rep can explicitly persist that stage's config
// against the customer created in Account Setup — so it can be reloaded later
// to validate what was configured, per stage.
import { useState } from 'react'
import { Check, Loader2, Save } from 'lucide-react'
import { platformJourneysApi } from '@api/client'
import type { PlatformJourneyUpdate } from '@api/client'

interface StageSaveBarProps {
  journeyId: number | null
  /** Built fresh on every render from the stage's current local state — only
   * read at the moment Save is clicked. */
  buildFields: () => PlatformJourneyUpdate
  onSaved: () => void
}

export function StageSaveBar({ journeyId, buildFields, onSaved }: StageSaveBarProps) {
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState<Date | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function handleSave() {
    if (!journeyId) return
    setSaving(true)
    setError(null)
    const res = await platformJourneysApi.update(journeyId, buildFields())
    setSaving(false)
    if (res.success) {
      setSavedAt(new Date())
      onSaved()
    } else {
      setError(res.error ?? 'Failed to save')
    }
  }

  if (!journeyId) {
    return (
      <p className="text-[10px] text-[#94a3b8] italic">
        Set up the customer in Account Setup first to save this configuration.
      </p>
    )
  }

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={() => void handleSave()}
        disabled={saving}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[11px] font-semibold transition-colors disabled:opacity-60"
        style={{ background: '#f1f5f9', border: '1px solid #283a57', color: '#4f46e5' }}
      >
        {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
        {saving ? 'Saving…' : 'Save configuration'}
      </button>
      {savedAt && !saving && (
        <span className="flex items-center gap-1 text-[10px] text-emerald-700">
          <Check size={11} /> Saved at {savedAt.toLocaleTimeString()}
        </span>
      )}
      {error && <span className="text-[10px] text-red-700">{error}</span>}
    </div>
  )
}
